/**
 * Constructed meta eras — derived from `SET_RELEASE_CALENDAR`, never a
 * second list of dates.
 *
 * Competitive Standard moves in discrete eras between English set releases
 * (Origins, Spiritforged, Unleashed, Vendetta, …). An era starts at that
 * expansion's `releaseDate` (inclusive, UTC) and ends when the next
 * `startsMeta` expansion releases (exclusive). Showcase (ARC), starter
 * products (OGS, PG2), and promo catalogs do not start an era.
 *
 * This is the match-history "which era was this game played in" clock.
 * It is not Standard card-pool legality: until Set 10 rotation (~2028),
 * older expansions remain legal; `validateDeck` does not use these windows.
 *
 * When a new expansion is announced, set `startsMeta: true` on its calendar
 * entry. Era bounds follow from that flag + the existing release date.
 */
import { SET_RELEASE_CALENDAR, calendarDayIso } from './setReleaseCalendar.js';

const DAY_MS = 24 * 60 * 60 * 1000;

function utcMidnightMs(isoDate) {
  return Date.parse(`${isoDate}T00:00:00.000Z`);
}

function addUtcDays(isoDate, days) {
  return new Date(utcMidnightMs(isoDate) + days * DAY_MS).toISOString().slice(0, 10);
}

function normalizeMetaCode(code) {
  return String(code || '').trim().toUpperCase();
}

/** Expansion codes whose release starts a constructed meta era, oldest first. */
export function constructedMetaSetCodes() {
  return SET_RELEASE_CALENDAR.filter((entry) => entry.startsMeta).map((entry) => entry.code);
}

export function isConstructedMetaSetCode(code) {
  const upper = normalizeMetaCode(code);
  return Boolean(upper) && constructedMetaSetCodes().includes(upper);
}

/**
 * Full constructed-meta timeline, including upcoming expansions.
 * `endDate` is exclusive (the next expansion's release day) or null for the
 * last calendar entry.
 */
export function constructedMetaPeriods() {
  const sets = SET_RELEASE_CALENDAR
    .filter((entry) => entry.startsMeta && entry.releaseDate)
    .slice()
    .sort((a, b) => a.releaseDate.localeCompare(b.releaseDate));

  return sets.map((set, index) => {
    const next = sets[index + 1];
    return {
      code: set.code,
      startDate: set.releaseDate,
      endDate: next?.releaseDate ?? null,
    };
  });
}

export function metaPeriodByCode(code) {
  const upper = normalizeMetaCode(code);
  if (!upper) return null;
  return constructedMetaPeriods().find((period) => period.code === upper) ?? null;
}

export function releasedMetaPeriods(today = calendarDayIso()) {
  return constructedMetaPeriods().filter((period) => period.startDate <= today);
}

export function currentMetaPeriod(today = calendarDayIso()) {
  const released = releasedMetaPeriods(today);
  return released[released.length - 1] ?? null;
}

/** Last inclusive UTC day of an era, or null when the era is still open. */
export function metaPeriodInclusiveEndDate(period) {
  if (!period?.endDate) return null;
  return addUtcDays(period.endDate, -1);
}

/**
 * Inclusive start / exclusive end as UTC epoch ms. `endMs` is null when the
 * calendar has no later meta-starting expansion.
 */
export function metaPeriodTimeRange(code) {
  const period = metaPeriodByCode(code);
  if (!period) return null;
  return {
    startMs: utcMidnightMs(period.startDate),
    endMs: period.endDate ? utcMidnightMs(period.endDate) : null,
  };
}

export function timestampInMetaPeriod(ms, code) {
  const range = metaPeriodTimeRange(code);
  if (range == null || !Number.isFinite(ms)) return false;
  if (ms < range.startMs) return false;
  if (range.endMs != null && ms >= range.endMs) return false;
  return true;
}

/** Newest released era first — what the match-history filter lists. */
export function metaPeriodFilterOptions(today = calendarDayIso()) {
  const current = currentMetaPeriod(today);
  return releasedMetaPeriods(today)
    .slice()
    .reverse()
    .map((period) => ({
      ...period,
      isCurrent: current?.code === period.code,
    }));
}
